import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { FilterElement, Label } from "@/components/search/filter_element";
import { watchMixin } from "@/components/meta/watch_mixin";
import { ClearInitialContentController } from "@/components/meta/clear_initial_content_controller";
import { generateUniqueId } from "@/utilities";
import { Tooltip } from "bootstrap";

type TabInfo = { id: string; name: string; title?: string; count?: number };

/**
 * This component inherits from FilterCollectionElement.
 * It represent a list of tabs, where each tab shows a filtered set of the search results
 *
 * @element d-filter-tabs
 *
 * @prop {{id: string, name: string, title: string}[]} labels - All labels that could potentially be selected
 * @prop {boolean} multi - Whether multiple tabs can be selected
 * @prop {string} param - The query parameter to use
 */
@customElement("d-filter-tabs")
export class FilterTabs extends watchMixin(FilterElement) {
    clearInitialContent = new ClearInitialContentController(this);

    @property()
    multi = false;
    @property()
    param = "tab";
    @property({ type: Array })
    labels: TabInfo[] = [];

    // unique per component instance to avoid duplicate ids if multiple tab sets appear on the page
    private readonly uniqueId = generateUniqueId();

    private onTabClick(e: Event): void {
        const btn = e.currentTarget as HTMLElement;
        const id = btn.dataset.tabId;
        const label = this.labels.find((l) => l.id === id);

        this.processClick(e, label);
        const tabs = this.renderRoot.querySelectorAll<HTMLElement>("[data-bs-toggle='tooltip']");
        tabs.forEach((tab) => {
            Tooltip.getInstance(tab)?.hide();
        });
    }

    processClick(e: Event, label: Label): void {
        if (!this.isSelected(label)) {
            this.select(label);
        }
        e.preventDefault();
    }

    watch = {
        labels: () => {
            if (this.getSelectedLabels().length === 0 && this.labels.length > 0) {
                this.select(this.labels[0]);
            }
        },
    };

    render(): TemplateResult {
        const selected = this.getSelectedLabels()[0]?.id;

        return html`
            <div class="card-tab">
                <ul class="nav nav-tabs" role="tablist">
                    ${this.labels.map((label) => {
                        const isActive = selected === label.id;
                        const tabId = `tab-${this.uniqueId}-${label.id}`;
                        const countId = `tab-${this.uniqueId}-${label.id}-count`;

                        return html`
                            <li role="presentation">
                                <a
                                    href="#"
                                    class="${isActive ? "active" : ""}"
                                    role="tab"
                                    aria-selected="${isActive ? "true" : "false"}"
                                    id="${tabId}"
                                    data-tab-id="${label.id}"
                                    data-bs-toggle=${ifDefined(label.title ? "tooltip" : undefined)}
                                    title=${ifDefined(label.title)}
                                    data-bs-trigger=${ifDefined(label.title ? "hover focus" : undefined)}
                                    @click=${this.onTabClick}
                                >
                                    ${label.name}
                                    ${
                                        label.count
                                            ? html`<span class="badge rounded-pill colored-secondary" id="${countId}">
                                                  ${label.count}
                                              </span>`
                                            : ""
                                    }
                                </a>
                            </li>
                        `;
                    })}
                </ul>
            </div>
        `;
    }
}
